/**
 * Top 3 señales prioritarias del hub de clínica (`/clinics/:tenantId` overview).
 *
 * Producto: al entrar, ≤3 cosas a mirar ya; el resto colapsado.
 * Umbrales simples — no inventar reglas en el JSX. Documentado en
 * `docs/CLINIC_HUB_TOP_SIGNALS.md`.
 *
 * ## Ranking (menor `priority` gana; máx. 3)
 * 1. Caída de ingresos crítica (≤ −15% MoM)
 * 2. PVP / retención en alert|critical (o ΔPVP / pros en atención)
 * 3. Caída de ingresos warning (≤ −5% MoM, no crítica)
 * 4. Ausentismo elevado (Δ ≥ +2 pp o rate ≥ 12%)
 * 5. Pacientes en riesgo (no_volvieron.total ≥ umbral)
 * 6. Caída de atendidos (≤ −5% MoM)
 *
 * ## CTA
 * Cada señal apunta a Finanzas, Pacientes, Profesionales o hub tab.
 * Diferencia vs owner insights (−10% ingresos): el hub usa −5% / −15% para
 * detectar antes en el contexto de una sola clínica.
 */

import type { AttentionCtaTarget } from '@/application/metrics/attentionCta';
import type { RetentionStatus } from '@/application/metrics/ownerPvpRetentionTypes';

/** Máximo de señales visibles en el bloque Top 3. */
export const HUB_TOP_SIGNAL_LIMIT = 3;

/** Δ ingresos MoM (%) → warning. */
export const HUB_REVENUE_WATCH_PCT = -5;
/** Δ ingresos MoM (%) → critical (prioridad sobre warning). */
export const HUB_REVENUE_CRITICAL_PCT = -15;
/** Δ atendidos MoM (%) → señal. */
export const HUB_PATIENTS_VOLUME_WATCH_PCT = -5;
/** Δ ausentismo (pp) → señal. */
export const HUB_ABSENTEEISM_DELTA_PP = 2;
/** Rate ausentismo (%) absoluto → señal. */
export const HUB_ABSENTEEISM_RATE_PCT = 12;
/** Δ PVP (pts) con ≥1 pro en atención → retención. */
export const HUB_PVP_DROP_PTS = -0.4;
/** Profesionales en atención (sin mirar Δ) → retención. */
export const HUB_PROS_IN_ATTENTION_ABS = 3;
/** Mínimo de pacientes no-volvieron para señal de riesgo. */
export const HUB_RISK_PATIENTS_MIN = 1;

export type HubTopSignalId =
  | 'revenue_critical'
  | 'retention'
  | 'revenue'
  | 'absenteeism'
  | 'risk'
  | 'patients_volume';

export type HubTopSignalSeverity = 'critical' | 'warning';

export type HubTopSignal = {
  id: HubTopSignalId;
  severity: HubTopSignalSeverity;
  /** Menor = más prioritario. */
  priority: number;
  cta: AttentionCtaTarget;
  /** Tab fina en /patients cuando cta === patients. */
  patientsTab?: 'risk' | 'overview';
  /** Valores para i18n (body). */
  metrics: {
    revenueDeltaPct?: number | null;
    patientsDeltaPct?: number | null;
    absenteeismRate?: number | null;
    pvpValue?: number | null;
    pvpDeltaPts?: number | null;
    prosInAttention?: number;
    riskCount?: number;
  };
};

export type BuildHubTopSignalsInput = {
  revenueDeltaPct: number | null;
  patientsDeltaPct: number | null;
  absenteeismRate: number | null;
  absenteeismDeltaPp: number | null;
  retentionStatus: RetentionStatus | null | undefined;
  pvpValue: number | null;
  pvpDeltaPts: number | null;
  professionalsInAttention: number;
  riskPatientsTotal: number | null;
  /** Si false, no emite señal risk (sin permiso / sin fetch). */
  includeRisk?: boolean;
};

const PRIORITY: Record<HubTopSignalId, number> = {
  revenue_critical: 0,
  retention: 1,
  revenue: 2,
  absenteeism: 3,
  risk: 4,
  patients_volume: 5,
};

export function retentionNeedsAttention(input: {
  status: RetentionStatus | null | undefined;
  pvpDeltaPts: number | null;
  professionalsInAttention: number;
}): boolean {
  const { status, pvpDeltaPts, professionalsInAttention } = input;
  return (
    status === 'critical' ||
    status === 'alert' ||
    (pvpDeltaPts !== null && pvpDeltaPts <= HUB_PVP_DROP_PTS && professionalsInAttention >= 1) ||
    professionalsInAttention >= HUB_PROS_IN_ATTENTION_ABS
  );
}

/**
 * Construye candidatos, ordena por prioridad y corta a {@link HUB_TOP_SIGNAL_LIMIT}.
 */
export function buildHubTopSignals(input: BuildHubTopSignalsInput): HubTopSignal[] {
  const candidates: HubTopSignal[] = [];
  const includeRisk = input.includeRisk !== false;

  if (input.revenueDeltaPct !== null && input.revenueDeltaPct <= HUB_REVENUE_CRITICAL_PCT) {
    candidates.push({
      id: 'revenue_critical',
      severity: 'critical',
      priority: PRIORITY.revenue_critical,
      cta: 'finances',
      metrics: { revenueDeltaPct: input.revenueDeltaPct },
    });
  } else if (input.revenueDeltaPct !== null && input.revenueDeltaPct <= HUB_REVENUE_WATCH_PCT) {
    candidates.push({
      id: 'revenue',
      severity: 'warning',
      priority: PRIORITY.revenue,
      cta: 'finances',
      metrics: { revenueDeltaPct: input.revenueDeltaPct },
    });
  }

  if (
    retentionNeedsAttention({
      status: input.retentionStatus,
      pvpDeltaPts: input.pvpDeltaPts,
      professionalsInAttention: input.professionalsInAttention,
    })
  ) {
    candidates.push({
      id: 'retention',
      severity: input.retentionStatus === 'critical' ? 'critical' : 'warning',
      priority: PRIORITY.retention,
      cta: 'professionals',
      metrics: {
        pvpValue: input.pvpValue,
        pvpDeltaPts: input.pvpDeltaPts,
        prosInAttention: input.professionalsInAttention,
      },
    });
  }

  if (
    (input.absenteeismDeltaPp !== null && input.absenteeismDeltaPp >= HUB_ABSENTEEISM_DELTA_PP) ||
    (input.absenteeismRate !== null && input.absenteeismRate >= HUB_ABSENTEEISM_RATE_PCT)
  ) {
    candidates.push({
      id: 'absenteeism',
      severity: 'warning',
      priority: PRIORITY.absenteeism,
      cta: 'patients',
      metrics: { absenteeismRate: input.absenteeismRate },
    });
  }

  if (
    includeRisk &&
    input.riskPatientsTotal !== null &&
    input.riskPatientsTotal >= HUB_RISK_PATIENTS_MIN
  ) {
    candidates.push({
      id: 'risk',
      severity: 'warning',
      priority: PRIORITY.risk,
      cta: 'patients',
      patientsTab: 'risk',
      metrics: { riskCount: input.riskPatientsTotal },
    });
  }

  if (
    input.patientsDeltaPct !== null &&
    input.patientsDeltaPct <= HUB_PATIENTS_VOLUME_WATCH_PCT
  ) {
    candidates.push({
      id: 'patients_volume',
      severity: 'warning',
      priority: PRIORITY.patients_volume,
      cta: 'patients',
      metrics: { patientsDeltaPct: input.patientsDeltaPct },
    });
  }

  return candidates.sort((a, b) => a.priority - b.priority).slice(0, HUB_TOP_SIGNAL_LIMIT);
}
